"use client";

import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { RecipeTile } from "@/components/shared/RecipeTile";
import { FlyInTile } from "@/components/ui/fly-in-tile";
import type { RecipeSearchPage, RecipeTileData } from "@/lib/recipes/types";

const BREAKPOINTS = [
  { min: 960, cols: 5 },
  { min: 720, cols: 4 },
  { min: 480, cols: 3 },
] as const;
const DEFAULT_COLS = 2;
const DEFAULT_ASPECT = 4 / 5;

function useContainerColumnCount(ref: React.RefObject<HTMLDivElement | null>) {
  const [count, setCount] = useState(DEFAULT_COLS);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth;
      let c = DEFAULT_COLS;
      for (const bp of BREAKPOINTS) {
        if (w >= bp.min) {
          c = bp.cols;
          break;
        }
      }
      setCount(c);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return count;
}

function distributeToColumns(items: RecipeTileData[], columnCount: number): RecipeTileData[][] {
  const cols: RecipeTileData[][] = Array.from({ length: columnCount }, () => []);
  const heights = new Array(columnCount).fill(0);

  for (const item of items) {
    const shortest = heights.indexOf(Math.min(...heights));
    cols[shortest].push(item);
    const ratio = item.image ? (item.imageAspectRatio ?? DEFAULT_ASPECT) : DEFAULT_ASPECT;
    heights[shortest] += 1 / ratio;
  }

  return cols;
}

interface InfiniteRecipeGridProps {
  initialItems: RecipeTileData[];
  initialHasMore: boolean;
  fetchUrl: string;
}

export function InfiniteRecipeGrid({
  initialItems,
  initialHasMore,
  fetchUrl,
}: InfiniteRecipeGridProps) {
  const [items, setItems] = useState(initialItems);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [loading, setLoading] = useState(false);
  const pageRef = useRef(1);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const columnCount = useContainerColumnCount(gridRef);

  useEffect(() => {
    setItems(initialItems);
    setHasMore(initialHasMore);
    pageRef.current = 1;
  }, [initialItems, initialHasMore]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const sep = fetchUrl.includes("?") ? "&" : "?";
      const res = await fetch(`${fetchUrl}${sep}page=${pageRef.current}`);
      if (!res.ok) return;
      const data = (await res.json()) as RecipeSearchPage;
      setItems((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        const fresh = data.items.filter((r) => !seen.has(r.id));
        return [...prev, ...fresh];
      });
      setHasMore(data.hasMore);
      pageRef.current++;
    } finally {
      setLoading(false);
    }
  }, [fetchUrl, hasMore, loading]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const columns = useMemo(() => distributeToColumns(items, columnCount), [items, columnCount]);

  return (
    <>
      <div
        ref={gridRef}
        className="grid gap-2.5"
        style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
      >
        {columns.map((col, colIdx) => (
          <div key={colIdx} className="flex flex-col gap-2.5">
            {col.map((r) => (
              <FlyInTile key={r.id}>
                <RecipeTile recipe={r} priority={colIdx === 0 && r === col[0]} />
              </FlyInTile>
            ))}
          </div>
        ))}
      </div>
      {hasMore && (
        <div ref={sentinelRef} className="mt-6 flex justify-center py-4">
          {loading && <Loader2 className="size-5 animate-spin text-muted-foreground" />}
        </div>
      )}
    </>
  );
}
